import React, { useState } from "react";
import type { InventoryItem, UsageRecord } from "../../types";
import { getJstToday, isToday } from "../../logic/dateUtils";
import ModalHeader from "./modalHeader";
import { CHEMICAL_MANAGEMENT_TYPE } from "../../constants/const";

interface UsageRecordModalProps {
  /** 薬剤在庫情報 */
  item: InventoryItem;
  /** 登録ボタンハンドラ */
  onSave: (record: UsageRecord) => void;
  /** キャンセルボタンハンドラ */
  onClose: () => void;
}

/**
 * 薬剤残量実績入力ポップアップ
 * @param props
 * @returns
 */
const UsageRecordModal: React.FC<UsageRecordModalProps> = ({ item, onSave, onClose }) => {
  // 登録日付
  const [date, setDate] = useState(getJstToday());
  // 残量
  const [actualUsage, setActualUsage] = useState<number>(
    isToday(item.date) ? item.volume ?? 0 : 0
  );
  // 貯槽残量リスト
  const [actualTankUsages, setActualTankUsages] = useState<number[]>(
    isToday(item.date) ? (item.stockVolumes ?? []) : Array(item.stockVolumes?.length).fill(0)
  );
  // 開封済み残量
  const [openedActualUsage, setOpenedActualUsage] = useState<number>(
    isToday(item.date) ? item.volumeOpenedBoxData ?? 0 : 0
  );
  // 空き箱数
  const [countEmptyBox, setCountEmptyBox] = useState<number>(
    isToday(item.date) ? item.countEmptyBox ?? 0 : 0
  );

  /**
   * 登録ボタン押下ハンドラ
   * @param e フォームイベント
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      itemId: item.id,
      date,
      actualUsage: Number(actualUsage),
      actualTankUsages: actualTankUsages.map(value => Number(value)),
      openedActualUsage: Number(openedActualUsage),
      emptyBoxNum: Number(countEmptyBox),
    });
  };

  return (
    <div className="modal" onClick={onClose}>
      <div className="content" onClick={e => e.stopPropagation()}>
        <ModalHeader title={`残量入力 - ${item.name}`} onClose={onClose} />

        <form onSubmit={handleSubmit}>
          <div className="parts">
            <label htmlFor="date">登録日付:</label>
            <input
              type="date"
              id="date"
              value={date}
              max={date}
              onChange={e => setDate(e.target.value)}
              required
            />
          </div>
          <div className="parts">
            <label htmlFor="actualUsage">残量 ({item.unit}):</label>
            <input
              type="number"
              id="actualUsage"
              value={actualUsage}
              onChange={e => setActualUsage(Number(e.target.value))}
              min="0"
              step="any"
              required
            />
          </div>
          {/* 管理用途が箱系＋貯槽系管理の場合 */}
          {item.type === CHEMICAL_MANAGEMENT_TYPE.BOX_AND_TANK &&
            actualTankUsages &&
            actualTankUsages.map((volume, index) => (
              <div className="parts" key={index}>
                <label htmlFor={`actualTankUsages${index}`}>貯槽残量{index + 1} (L):</label>
                <input
                  type="number"
                  id={`actualTankUsages${index}`}
                  value={volume}
                  onChange={e =>
                    setActualTankUsages(prev =>
                      prev.map((s, i) => (i === index ? Number(e.target.value) : s))
                    )
                  }
                  min="0"
                  step="any"
                  required
                />
              </div>
            ))}
          {/* 管理用途が箱系管理もしくは貯槽系＋箱系管理の場合 */}
          {(item.type === CHEMICAL_MANAGEMENT_TYPE.BOX ||
            item.type === CHEMICAL_MANAGEMENT_TYPE.BOX_AND_TANK) && (
            <>
              <div className="parts">
                <label htmlFor="openedActualUsage">開封済み残量 ({item.unit2}):</label>
                <input
                  type="number"
                  id="openedActualUsage"
                  value={openedActualUsage}
                  onChange={e => setOpenedActualUsage(Number(e.target.value))}
                  min="0"
                  step="any"
                  required
                />
              </div>
              <div className="parts">
                <label htmlFor="countEmptyBox">
                  空き{item.unit}数 ({item.unit}):
                </label>
                <input
                  type="number"
                  id="countEmptyBox"
                  value={countEmptyBox}
                  onChange={e => setCountEmptyBox(Number(e.target.value))}
                  min="0"
                  step="any"
                  required
                />
              </div>
            </>
          )}

          <div className="parts actions">
            <button type="button" onClick={onClose} className="negative">
              キャンセル
            </button>
            <button type="submit" className="positive">
              登録
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsageRecordModal;
